import { useState } from 'react';
import { Colors, StepStatus } from '@/shared/types/types';
import { StepperStep, ColorClasses } from '@/shared/types/stepper';

interface UseStepperProps {
  steps: StepperStep[];
  currentStep?: number;
  onClickStep?: (step: number) => void;
  onStepChange?: (step: number) => void;
}

/**
 * Hook para manejar la lógica del Stepper
 * Usa tokens semánticos de Figma para colores
 */
export const useStepper = ({
  steps,
  currentStep: externalCurrentStep,
  onClickStep,
  onStepChange,
}: UseStepperProps) => {
  const [internalCurrentStep, setInternalCurrentStep] = useState(0);
  const currentStep = externalCurrentStep !== undefined ? externalCurrentStep : internalCurrentStep;

  /**
   * Mapa de colores usando tokens semánticos de Figma
   * - primary: bg y border para estado completado/activo (fill)
   * - secondary: border para estado pendiente (surface)
   * - text: color del número (pendiente); en azul/rojo/verde/naranja/amarillo usa tokens icon Figma (`ui-text-icon-all-colors-*`)
   * - line: background color del conector entre pasos
   */
  const getColorClasses = (color: Colors): ColorClasses => {
    const colorMap: Record<Colors, ColorClasses> = {
      blue: {
        primary: 'ui-bg-fill-brand ui-border-all-colors-blue',
        secondary: 'ui-border-all-colors-blue',
        text: 'ui-text-icon-all-colors-blue',
        line: 'ui-bg-fill-brand',
      },
      red: {
        primary: 'ui-bg-fill-danger ui-border-all-colors-red',
        secondary: 'ui-border-all-colors-red',
        text: 'ui-text-icon-all-colors-red',
        line: 'ui-bg-fill-danger',
      },
      green: {
        primary: 'ui-bg-fill-success ui-border-all-colors-green',
        secondary: 'ui-border-all-colors-green',
        text: 'ui-text-icon-all-colors-green',
        line: 'ui-bg-fill-success',
      },
      orange: {
        primary: 'ui-bg-fill-warning ui-border-all-colors-orange',
        secondary: 'ui-border-all-colors-orange',
        text: 'ui-text-icon-all-colors-orange',
        line: 'ui-bg-fill-warning',
      },
      yellow: {
        primary: 'ui-bg-fill-all-colors-yellow ui-border-all-colors-yellow',
        secondary: 'ui-border-all-colors-yellow',
        text: 'ui-text-icon-all-colors-yellow',
        line: 'ui-bg-fill-all-colors-yellow',
      },
      'blue-dark': {
        primary: 'ui-bg-blue-700 ui-border-blue-700',
        secondary: 'ui-border-blue-400',
        text: 'ui-text-blue-700',
        line: 'ui-bg-fill-blue-dark',
      },
      'blue-light': {
        primary: 'ui-bg-blue-300 ui-border-blue-300',
        secondary: 'ui-border-blue-200',
        text: 'ui-text-blue-400',
        line: 'ui-bg-fill-blue-light',
      },
      neutral: {
        primary: 'ui-bg-fill-secondary ui-border-DEFAULT',
        secondary: 'ui-border-DEFAULT',
        text: 'ui-text-secondary',
        line: 'ui-bg-fill-neutral',
      },
      black: {
        primary: 'ui-bg-canvas-inverse ui-border-strong',
        secondary: 'ui-border-strong',
        text: 'ui-text-primary',
        line: 'ui-bg-fill-black',
      },
      white: {
        primary: 'ui-bg-fill ui-border-subtle',
        secondary: 'ui-border-subtle',
        text: 'ui-text-tertiary',
        line: 'ui-bg-fill-white',
      },
      transparent: {
        primary: 'ui-bg-transparent ui-border-transparent',
        secondary: 'ui-border-transparent',
        text: 'ui-text-transparent',
        line: 'ui-bg-fill-transparent',
      },
    };
    return colorMap[color] || colorMap.blue;
  };

  const handleStepClick = (stepIndex: number) => {
    if (onClickStep) {
      onClickStep(stepIndex);
    }
    if (externalCurrentStep === undefined) {
      setInternalCurrentStep(stepIndex);
    }
    if (onStepChange) {
      onStepChange(stepIndex);
    }
  };

  const getStepStatus = (stepIndex: number): StepStatus => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'active';
    return 'pending';
  };

  return {
    currentStep,
    getColorClasses,
    handleStepClick,
    getStepStatus,
  };
};
